export type LegacyLinkageStatus =
  | 'RESOLVED'
  | 'PENDING_REVIEW'
  | 'UNRESOLVED'
  | 'UNAVAILABLE'
  | string;

export type LegacyLinkagePiiSnapshot = {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
};

export type LegacyLinkageJourney = {
  status?: LegacyLinkageStatus;
  powerId?: string | null;
  reviewId?: string | null;
  candidateCount?: number | null;
  stampedAt?: string | null;
  source?: string | null;
  matchMethod?: string | null;
  distance?: number | null;
  fundingBlocked?: boolean;
  isMinted?: boolean;
  liveRead?: boolean;
  legacyPii?: LegacyLinkagePiiSnapshot | null;
  candidates?: Array<{
    powerId: string;
    distance: number | null;
    rank: number;
    piiCorroborated?: boolean | null;
    piiReason?: string | null;
    legacyPii?: LegacyLinkagePiiSnapshot | null;
    applicantPii?: LegacyLinkagePiiSnapshot | null;
    givenNameMatch?: boolean | null;
    surnameMatch?: boolean | null;
    dobMatch?: boolean | null;
  }>;
};

export function normalizeLinkageStatus(linkage?: LegacyLinkageJourney | null): string {
  return (linkage?.status ?? 'UNRESOLVED').toUpperCase();
}

export function linkageStatusLabel(status: string): string {
  switch (status) {
    case 'RESOLVED':
      return 'Linked';
    case 'PENDING_REVIEW':
      return 'Review required';
    case 'UNAVAILABLE':
      return 'Service unavailable';
    case 'UNRESOLVED':
      return 'Not linked yet';
    default:
      return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }
}

export function linkageStatusChipColor(
  status: string,
): 'success' | 'warning' | 'error' | 'default' {
  if (status === 'RESOLVED') return 'success';
  if (status === 'PENDING_REVIEW') return 'warning';
  if (status === 'UNAVAILABLE') return 'error';
  return 'default';
}

export function linkageSourceLabel(linkage?: LegacyLinkageJourney | null): string | null {
  if (linkage?.isMinted || linkage?.source === 'MINTED') {
    return 'New EvolveOS customer ID';
  }
  if (linkage?.source === 'LEGACY_MATCH') {
    return 'Matched existing legacy customer';
  }
  if (linkage?.source) return linkage.source.replace(/_/g, ' ').toLowerCase();
  return null;
}

export function linkageMatchMethodLabel(matchMethod?: string | null): string | null {
  if (!matchMethod) return null;
  switch (matchMethod) {
    case 'AUTO_VECTOR_PII':
      return 'Auto (face + name + DOB)';
    case 'REVIEWER':
      return 'Manual selection';
    case 'MINTED_NO_CANDIDATES':
      return 'Minted — no legacy match';
    case 'MINTED_REVIEWER_REJECTED':
      return 'Minted — reviewer rejected candidates';
    default:
      return matchMethod.replace(/_/g, ' ').toLowerCase();
  }
}

export function formatLinkageDistance(distance?: number | null): string | null {
  if (typeof distance !== 'number' || !Number.isFinite(distance)) return null;
  return distance.toFixed(3);
}

export function formatLinkageStamp(stampedAt?: string | null, liveRead?: boolean): string | null {
  if (!stampedAt) return liveRead ? 'Live read' : null;
  const when = new Date(stampedAt);
  if (Number.isNaN(when.getTime())) return liveRead ? 'Live read' : null;
  const formatted = when.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return liveRead ? `${formatted} (live)` : formatted;
}

export function linkageFundingLabel(linkage?: LegacyLinkageJourney | null): {
  label: string;
  color: 'success' | 'warning' | 'error' | 'info';
  variant: 'filled' | 'outlined';
} {
  const status = normalizeLinkageStatus(linkage);
  if (status === 'RESOLVED') {
    return { label: 'Ready to fund', color: 'success', variant: 'filled' };
  }
  if (status === 'PENDING_REVIEW') {
    return { label: 'Action required', color: 'info', variant: 'outlined' };
  }
  if (status === 'UNAVAILABLE') {
    return { label: 'Retry needed', color: 'error', variant: 'outlined' };
  }
  return { label: 'Link pending', color: 'warning', variant: 'outlined' };
}

export function linkageSummaryLine(linkage?: LegacyLinkageJourney | null): string {
  const status = normalizeLinkageStatus(linkage);
  const label = linkageStatusLabel(status);
  if (status === 'RESOLVED' && linkage?.powerId) {
    const kind = linkage.isMinted ? 'new customer ID' : 'legacy match';
    return `${label} · legacy customer ${linkage.powerId} (${kind})`;
  }
  return label;
}

export function legacyLinkageBlocksFunding(linkage?: LegacyLinkageJourney | null): boolean {
  if (!linkage) return false;
  if (linkage.fundingBlocked === true) return true;
  const status = normalizeLinkageStatus(linkage);
  return status !== 'RESOLVED';
}

export type LinkageOverviewAlert = {
  id: string;
  service: string;
  title: string;
  detail: string;
  severity: 'warning' | 'error' | 'info';
  tabTarget: 'identity';
};

/** Overview alert when linkage blocks funding or needs operator action. */
export function resolveLinkageOverviewAlert(
  linkage?: LegacyLinkageJourney | null,
): LinkageOverviewAlert | null {
  if (!linkage) return null;
  const status = normalizeLinkageStatus(linkage);
  if (status === 'RESOLVED') return null;

  if (status === 'PENDING_REVIEW') {
    return {
      id: 'legacy-linkage-pending-review',
      service: 'Identity',
      title: 'Legacy customer link needs review',
      detail:
        'Funding is blocked until an operator confirms the legacy record or assigns a new customer ID.',
      severity: 'warning',
      tabTarget: 'identity',
    };
  }

  if (status === 'UNAVAILABLE') {
    return {
      id: 'legacy-linkage-unavailable',
      service: 'Identity',
      title: 'Legacy customer link unavailable',
      detail: 'Automatic linking failed — retry from the Identity tab when the service is healthy.',
      severity: 'error',
      tabTarget: 'identity',
    };
  }

  if (status === 'UNRESOLVED') {
    return {
      id: 'legacy-linkage-unresolved',
      service: 'Identity',
      title: 'Legacy customer link pending',
      detail: 'Funding stays blocked until this application is linked to a Cash4You customer record.',
      severity: 'warning',
      tabTarget: 'identity',
    };
  }

  return null;
}

export function piiMatchLabel(
  corroborated?: boolean | null,
  reason?: string | null,
): string {
  const friendlyReason = reason ? mapPiiReasonForDisplay(reason) : null;
  if (corroborated === true) return 'First name + DOB match';
  if (corroborated === false) {
    return friendlyReason ? `PII mismatch (${friendlyReason})` : 'PII mismatch';
  }
  return friendlyReason ?? reason ?? 'PII unknown';
}

function mapPiiReasonForDisplay(reason: string): string {
  switch (reason) {
    case 'given name differs':
      return 'first name differs';
    case 'given name missing on one side':
      return 'first name missing on one side';
    default:
      return reason;
  }
}

export function decisionEnginePowerIdMismatch(
  linkage?: LegacyLinkageJourney | null,
  decisionEnginePowerId?: string | null,
): boolean {
  if (!linkage?.powerId || !decisionEnginePowerId) return false;
  return linkage.powerId.trim() !== decisionEnginePowerId.trim();
}

export type LegacyLinkageReviewCandidate = NonNullable<
  LegacyLinkageJourney['candidates']
>[number];

function parsePiiFromRaw(raw: unknown): {
  piiCorroborated: boolean | null;
  piiReason: string | null;
} {
  if (!raw || typeof raw !== 'object') {
    return { piiCorroborated: null, piiReason: null };
  }
  const rec = raw as Record<string, unknown>;
  const piiCorroborated =
    typeof rec.corroborated === 'boolean'
      ? rec.corroborated
      : typeof rec.givenNameMatch === 'boolean' && typeof rec.dobMatch === 'boolean'
        ? rec.givenNameMatch && rec.dobMatch
        : null;
  const piiReason = typeof rec.reason === 'string' ? rec.reason : null;
  return { piiCorroborated, piiReason };
}

function parsePiiSnapshot(raw: unknown): LegacyLinkagePiiSnapshot | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const firstName = typeof rec.firstName === 'string' ? rec.firstName.trim() || null : null;
  const lastName = typeof rec.lastName === 'string' ? rec.lastName.trim() || null : null;
  const dateOfBirth =
    typeof rec.dateOfBirth === 'string' ? rec.dateOfBirth.trim() || null : null;
  if (!firstName && !lastName && !dateOfBirth) return null;
  return { firstName, lastName, dateOfBirth };
}

/** Normalize mapping API / journey candidate rows into panel-ready shape. */
export function normalizeLinkageCandidates(raw: unknown): LegacyLinkageReviewCandidate[] {
  if (!Array.isArray(raw)) return [];
  const out: LegacyLinkageReviewCandidate[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const powerId =
      typeof rec.powerId === 'string'
        ? rec.powerId
        : typeof rec.power_id === 'string'
          ? rec.power_id
          : rec.powerId != null
            ? String(rec.powerId)
            : rec.power_id != null
              ? String(rec.power_id)
              : null;
    if (!powerId) continue;
    const distance =
      typeof rec.distance === 'number' && Number.isFinite(rec.distance) ? rec.distance : null;
    const rank = typeof rec.rank === 'number' && Number.isFinite(rec.rank) ? rec.rank : out.length + 1;
    const pii =
      rec.piiCorroborated !== undefined || rec.piiReason !== undefined
        ? {
            piiCorroborated:
              typeof rec.piiCorroborated === 'boolean' ? rec.piiCorroborated : null,
            piiReason: typeof rec.piiReason === 'string' ? rec.piiReason : null,
          }
        : parsePiiFromRaw(rec.piiMatch);
    out.push({
      powerId,
      distance,
      rank,
      piiCorroborated: pii.piiCorroborated,
      piiReason: pii.piiReason,
      legacyPii: parsePiiSnapshot(rec.legacyPii),
      applicantPii: parsePiiSnapshot(rec.applicantPii),
      givenNameMatch: typeof rec.givenNameMatch === 'boolean' ? rec.givenNameMatch : null,
      surnameMatch: typeof rec.surnameMatch === 'boolean' ? rec.surnameMatch : null,
      dobMatch: typeof rec.dobMatch === 'boolean' ? rec.dobMatch : null,
    });
  }
  return out.sort((a, b) => a.rank - b.rank);
}

export function mergeLinkageCandidates(
  primary: LegacyLinkageReviewCandidate[] | undefined,
  fallback: LegacyLinkageReviewCandidate[] | undefined,
): LegacyLinkageReviewCandidate[] {
  const merged = normalizeLinkageCandidates(primary);
  if (merged.length > 0) return merged;
  return normalizeLinkageCandidates(fallback);
}

/** Legacy MSSQL identity for a resolved link (journey field or matched candidate). */
export function resolveLinkedLegacyPii(
  linkage?: LegacyLinkageJourney | null,
): LegacyLinkagePiiSnapshot | null {
  if (linkage?.legacyPii) return linkage.legacyPii;
  const powerId = linkage?.powerId?.trim();
  if (!powerId || !linkage?.candidates?.length) return null;
  const matched = linkage.candidates.find((c) => c.powerId === powerId);
  return matched?.legacyPii ?? linkage.candidates[0]?.legacyPii ?? null;
}

export function pendingReviewCandidateCount(
  linkage?: LegacyLinkageJourney | null,
  candidates?: LegacyLinkageReviewCandidate[],
): number | null {
  const count = candidates?.length ?? 0;
  if (count > 0) return count;
  if (typeof linkage?.candidateCount === 'number' && linkage.candidateCount >= 0) {
    return linkage.candidateCount;
  }
  return null;
}
